import React, { useCallback, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaWhatsapp } from 'react-icons/fa';
import { MdDelete, MdEdit, MdLightbulbOutline } from 'react-icons/md';
import api from '../../services/api';
import { ApiError } from '../../services/tipos';

interface SituacaoAssistente {
    habilitado: boolean;
    nivel?: number | null;
    nivelNome?: string | null;
    telefone?: string;
    verificadoEm?: string;
}

interface InstrucaoAssistente {
    id: string;
    texto: string;
    ordem: number;
    origem: string;
    criadoEm: string;
}

const NIVEL_AVANCADO = 3;

const mensagemDeErro = (error: unknown, padrao: string) =>
    (error as AxiosError<ApiError>).response?.data?.Mensagem || padrao;

const formatarData = (valor?: string) => {
    if (!valor) return '';
    const data = new Date(valor.endsWith('Z') ? valor : `${valor}Z`);
    return data.toLocaleDateString('pt-BR');
};

const descricaoDoNivel = (nivel?: number | null) => {
    if (nivel === 1) return 'Modo Básico: o assistente responde consultas sobre a locadora.';
    if (nivel === 2) return 'Modo Intermediário: consultas e ações (cadastrar, alterar, dar baixa) sempre com a sua confirmação.';
    if (nivel === NIVEL_AVANCADO) return 'Modo Avançado: consultas, ações com confirmação, leitura de fotos, PDFs e áudios e instruções próprias da empresa.';
    return '';
};

const AssistenteWhatsAppSecao: React.FC = () => {
    const [situacao, setSituacao] = useState<SituacaoAssistente | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [telefone, setTelefone] = useState('');
    const [codigo, setCodigo] = useState('');
    const [aguardandoCodigo, setAguardandoCodigo] = useState(false);
    const [trocando, setTrocando] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const [instrucoes, setInstrucoes] = useState<InstrucaoAssistente[]>([]);
    const [maximoInstrucoes, setMaximoInstrucoes] = useState(20);
    const [tamanhoMaximo, setTamanhoMaximo] = useState(500);
    const [novaInstrucao, setNovaInstrucao] = useState('');
    const [editandoId, setEditandoId] = useState<string | null>(null);
    const [textoEdicao, setTextoEdicao] = useState('');
    const [salvandoInstrucao, setSalvandoInstrucao] = useState(false);

    const carregarInstrucoes = useCallback(async () => {
        try {
            const res = await api.get('/AssistenteInstrucao');
            setInstrucoes(res.data?.instrucoes ?? []);
            setMaximoInstrucoes(res.data?.maximo ?? 20);
            setTamanhoMaximo(res.data?.tamanhoMaximo ?? 500);
        } catch {
            toast.error('Não foi possível carregar as instruções do assistente.');
        }
    }, []);

    useEffect(() => {
        api.get<SituacaoAssistente>('/AssistenteWhatsApp/MeuTelefone')
            .then((res) => {
                setSituacao(res.data);
                if (res.data?.nivel === NIVEL_AVANCADO) carregarInstrucoes();
            })
            .catch(() => setSituacao(null))
            .finally(() => setCarregando(false));
    }, [carregarInstrucoes]);

    if (carregando) return null;

    if (!situacao?.habilitado) {
        return (
            <div className="max-w-3xl rounded-md border border-[#1F467E]/30 bg-[#EEF3FA] p-4 text-sm text-gray-900">
                O assistente no WhatsApp ainda não está liberado para a sua empresa. Fale com o suporte do LocaSmartPro para ativar.
            </div>
        );
    }

    const verificado = !!situacao.telefone && !trocando;
    const avancado = situacao.nivel === NIVEL_AVANCADO;

    const handleEnviarCodigo = async () => {
        if (telefone.replace(/\D/g, '').length < 10) {
            toast.error('Informe o celular com DDD.');
            return;
        }
        setEnviando(true);
        try {
            await api.post('/AssistenteWhatsApp/EnviarCodigo', { telefone });
            setAguardandoCodigo(true);
            setCodigo('');
            toast.success('Enviamos um código de 6 dígitos para o seu WhatsApp.');
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível enviar o código.'));
        } finally {
            setEnviando(false);
        }
    };

    const handleConfirmar = async () => {
        if (codigo.replace(/\D/g, '').length !== 6) {
            toast.error('Digite o código de 6 dígitos.');
            return;
        }
        setEnviando(true);
        try {
            const res = await api.post<SituacaoAssistente>('/AssistenteWhatsApp/ConfirmarCodigo', { codigo });
            setSituacao(res.data);
            setAguardandoCodigo(false);
            setTrocando(false);
            setTelefone('');
            setCodigo('');
            toast.success('Número verificado.');
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível confirmar o código.'));
        } finally {
            setEnviando(false);
        }
    };

    const handleRemover = async () => {
        setEnviando(true);
        try {
            await api.delete('/AssistenteWhatsApp/MeuTelefone');
            setSituacao({ ...situacao, telefone: undefined, verificadoEm: undefined });
            toast.success('Número removido.');
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível remover o número.'));
        } finally {
            setEnviando(false);
        }
    };

    const handleAdicionarInstrucao = async () => {
        const texto = novaInstrucao.trim();
        if (texto.length < 3) {
            toast.error('Escreva a instrução em pelo menos 3 caracteres.');
            return;
        }
        setSalvandoInstrucao(true);
        try {
            await api.post('/AssistenteInstrucao', { texto });
            setNovaInstrucao('');
            await carregarInstrucoes();
            toast.success('Instrução guardada. O assistente passa a usá-la nas próximas conversas.');
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível guardar a instrução.'));
        } finally {
            setSalvandoInstrucao(false);
        }
    };

    const handleSalvarEdicao = async () => {
        if (!editandoId) return;
        setSalvandoInstrucao(true);
        try {
            await api.put(`/AssistenteInstrucao/${editandoId}`, { texto: textoEdicao.trim() });
            setEditandoId(null);
            await carregarInstrucoes();
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível alterar a instrução.'));
        } finally {
            setSalvandoInstrucao(false);
        }
    };

    const handleRemoverInstrucao = async (instrucao: InstrucaoAssistente) => {
        setSalvandoInstrucao(true);
        try {
            await api.delete(`/AssistenteInstrucao/${instrucao.id}`);
            await carregarInstrucoes();
        } catch (error) {
            toast.error(mensagemDeErro(error, 'Não foi possível remover a instrução.'));
        } finally {
            setSalvandoInstrucao(false);
        }
    };

    const classeCampo = 'w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100';
    const classeBotao = 'px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors disabled:opacity-60';
    const classeLink = 'text-sm text-[#1F467E] hover:underline focus:outline-none disabled:opacity-60';
    const classeIcone = 'p-1 rounded text-[#1F467E] hover:bg-[#EEF3FA] disabled:opacity-60';

    return (
        <div className="max-w-3xl">
            <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3 flex items-center gap-2">
                <FaWhatsapp className="text-[#1F467E]" /> Assistente no WhatsApp
            </h3>
            {!verificado && (
                <div className="mb-4 rounded-md border-l-4 border-[#1F467E] bg-[#EEF3FA] p-4 text-sm text-gray-900">
                    Cadastre o seu celular para perguntar sobre a sua locadora pelo WhatsApp, por exemplo
                    <strong> "quem está em atraso?"</strong> ou <strong>"quais veículos estão livres?"</strong>.
                    As respostas respeitam as permissões do seu usuário.
                    {situacao.nivelNome && (
                        <div className="mt-2 text-xs text-[#1F467E]">
                            <strong>{descricaoDoNivel(situacao.nivel)}</strong>
                        </div>
                    )}
                </div>
            )}

            {verificado ? (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-2 text-black dark:text-gray-100">
                        <FaCheckCircle className="text-green-600" />
                        {situacao.telefone}
                        <span className="text-sm text-gray-500">verificado em {formatarData(situacao.verificadoEm)}</span>
                    </span>
                    <button onClick={() => setTrocando(true)} className={classeLink} disabled={enviando}>Trocar número</button>
                    <button onClick={handleRemover} className={classeLink} disabled={enviando}>Remover número</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">Celular com DDD</label>
                        <InputMask
                            mask="(99) 99999-9999"
                            value={telefone}
                            onChange={(e) => setTelefone(e.target.value)}
                            disabled={enviando}
                            className={classeCampo}
                            placeholder="(00) 00000-0000"
                        />
                        <div className="flex gap-3 pt-1">
                            <button onClick={handleEnviarCodigo} className={classeBotao} disabled={enviando}>
                                {aguardandoCodigo ? 'Reenviar código' : 'Verificar número'}
                            </button>
                            {trocando && (
                                <button onClick={() => { setTrocando(false); setAguardandoCodigo(false); }} className={classeLink} disabled={enviando}>
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </div>

                    {aguardandoCodigo && (
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">Código recebido</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                                disabled={enviando}
                                className={classeCampo}
                                placeholder="000000"
                            />
                            <div className="pt-1">
                                <button onClick={handleConfirmar} className={classeBotao} disabled={enviando}>Confirmar código</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {avancado && (
                <div className="mt-8">
                    <h4 className="text-base font-medium text-black dark:text-gray-100 mb-1 flex items-center gap-2">
                        <MdLightbulbOutline className="text-[#1F467E]" /> Instruções do assistente
                        <span className="text-sm font-normal text-gray-700 dark:text-gray-300">{instrucoes.length} de {maximoInstrucoes}</span>
                    </h4>
                    <p className="text-sm text-black dark:text-gray-100 mb-3">
                        O que você escrever aqui vale para todas as conversas da empresa, como se fosse a memória do assistente.
                        Também dá para pedir no próprio WhatsApp: "lembre que a vistoria é toda segunda".
                    </p>

                    {instrucoes.length > 0 && (
                        <ol className="mb-3 space-y-2">
                            {instrucoes.map((instrucao, indice) => (
                                <li key={instrucao.id} className="flex items-start gap-2 rounded-md border border-gray-200 dark:border-gray-700 p-2 text-sm">
                                    <span className="w-6 shrink-0 text-right text-gray-500">{indice + 1}.</span>
                                    {editandoId === instrucao.id ? (
                                        <div className="flex-1 space-y-2">
                                            <textarea
                                                value={textoEdicao}
                                                maxLength={tamanhoMaximo}
                                                onChange={(e) => setTextoEdicao(e.target.value)}
                                                className={classeCampo}
                                                rows={2}
                                                disabled={salvandoInstrucao}
                                            />
                                            <div className="flex gap-3">
                                                <button onClick={handleSalvarEdicao} className={classeBotao} disabled={salvandoInstrucao}>Salvar</button>
                                                <button onClick={() => setEditandoId(null)} className={classeLink} disabled={salvandoInstrucao}>Cancelar</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex-1 text-black dark:text-gray-100">
                                                {instrucao.texto}
                                                {instrucao.origem === 'assistente' && (
                                                    <span className="ml-2 text-xs text-gray-500">guardada pelo WhatsApp</span>
                                                )}
                                            </div>
                                            <button
                                                className={classeIcone}
                                                title="Alterar"
                                                disabled={salvandoInstrucao}
                                                onClick={() => { setEditandoId(instrucao.id); setTextoEdicao(instrucao.texto); }}
                                            >
                                                <MdEdit size={18} />
                                            </button>
                                            <button
                                                className={classeIcone}
                                                title="Remover"
                                                disabled={salvandoInstrucao}
                                                onClick={() => handleRemoverInstrucao(instrucao)}
                                            >
                                                <MdDelete size={18} />
                                            </button>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}

                    {instrucoes.length < maximoInstrucoes ? (
                        <div className="space-y-2">
                            <textarea
                                value={novaInstrucao}
                                maxLength={tamanhoMaximo}
                                onChange={(e) => setNovaInstrucao(e.target.value)}
                                className={classeCampo}
                                rows={2}
                                placeholder="Ex.: Responda sempre com o valor total já com juros e multa."
                                disabled={salvandoInstrucao}
                            />
                            <div className="flex items-center gap-3">
                                <button onClick={handleAdicionarInstrucao} className={classeBotao} disabled={salvandoInstrucao}>
                                    {salvandoInstrucao ? 'Salvando...' : 'Guardar instrução'}
                                </button>
                                <span className="text-xs text-gray-700 dark:text-gray-300">{novaInstrucao.length} de {tamanhoMaximo} caracteres</span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">Você chegou ao máximo de {maximoInstrucoes} instruções. Remova uma para guardar outra.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssistenteWhatsAppSecao;
